/**
 * Inplace Mount Panel (Spec 204 — US1 / FR-001/002)
 *
 * Renders the query-in-place section in the restore browser. Lists active mounts for
 * the selected asset, lets operators create a new mount (picks target workspace, optional
 * TTL), and tear down existing mounts. Lakehouse-only.
 *
 * Author: Peter Dobler (Dobler Data Solutions)
 * Feature: 204-instant-granular-recovery
 * Constitution: v1.22.0 | Principle XLV (Design System)
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Zap, Trash2, Copy, Plus } from 'lucide-react';
import { useToast } from '@/hooks/useToast';
import {
  Card,
  DataTable,
  Badge,
  Button,
  Select,
  Input,
  EmptyState,
  LoadingState,
  ErrorState,
} from '@/components/ui';
import { AppModal } from '@/components/ui/AppModal';
import { mountApi, InplaceMount, RestoreTarget } from '../../api/restore';
import { useAuth } from '../../hooks/useAuth';
import { restoreApi } from '../../api/restore';

function fmtExpiry(expiresAt: string): string {
  const diff = Math.max(0, new Date(expiresAt).getTime() - Date.now());
  const h = Math.floor(diff / 3_600_000);
  const m = Math.floor((diff % 3_600_000) / 60_000);
  if (diff <= 0) return 'Expired';
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function expiryVariant(expiresAt: string): 'success' | 'warning' | 'danger' {
  const h = (new Date(expiresAt).getTime() - Date.now()) / 3_600_000;
  if (h <= 0) return 'danger';
  if (h < 2)  return 'warning';
  return 'success';
}

interface CreateMountModalProps {
  entityId: string;
  runId: string;
  onClose: () => void;
}

const CreateMountModal: React.FC<CreateMountModalProps> = ({ entityId, runId, onClose }) => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [targetWorkspaceId, setTargetWorkspaceId] = useState('');
  const [ttlHours, setTtlHours] = useState('');
  const [copied, setCopied] = useState(false);
  const [createdName, setCreatedName] = useState<string | null>(null);

  const { data: targets } = useQuery<RestoreTarget[]>({
    queryKey: ['restore-targets'],
    queryFn: restoreApi.restoreTargets,
    staleTime: 60_000,
  });

  const create = useMutation({
    mutationFn: () =>
      mountApi.create('lakehouse', entityId, runId, targetWorkspaceId, ttlHours ? Number(ttlHours) : undefined),
    onSuccess: (mount) => {
      setCreatedName(mount.shortcutName);
      toast.success(`Mount ${mount.shortcutName} is provisioning — it will turn Active shortly.`);
      void queryClient.invalidateQueries({ queryKey: ['inplace-mounts'] });
    },
    onError: (e) => {
      toast.error(e instanceof Error ? e.message : 'Mount creation failed');
    },
  });

  const copyName = () => {
    if (createdName) {
      void navigator.clipboard.writeText(createdName);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <AppModal open title="Query in place" onClose={onClose}>
      {createdName ? (
        <div className="space-y-4">
          <p className="text-sm text-[var(--color-text-primary)]">
            Mount is provisioning. It will appear in the panel below and turn
            <span className="font-medium text-[var(--color-success)]"> Active</span> in ~30–60s.
            Open the target workspace and look for the lakehouse:
          </p>
          <div className="flex items-center gap-2 rounded border border-[var(--color-border)] bg-[var(--color-bg-secondary)] px-3 py-2">
            <span className="flex-1 font-mono text-sm">{createdName}</span>
            <Button size="sm" variant="ghost" onClick={copyName}>
              <Copy className="h-3.5 w-3.5 mr-1" />
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>
          <Button variant="secondary" onClick={onClose}>Done</Button>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-[var(--color-text-secondary)]">
            A read-only lakehouse with shortcuts to this recovery point&apos;s tables will be created
            in the selected workspace. Tear it down when done.
          </p>
          <div>
            <label className="mb-1 block text-xs font-medium text-[var(--color-text-secondary)]">
              Target workspace *
            </label>
            <Select
              value={targetWorkspaceId}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setTargetWorkspaceId(e.target.value)}
              options={[
                { value: '', label: 'Select workspace…' },
                ...(targets ?? []).map((t) => ({ value: t.workspaceId, label: t.workspaceName })),
              ]}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-[var(--color-text-secondary)]">
              TTL (hours, default 24)
            </label>
            <Input
              type="number"
              min={1}
              max={168}
              placeholder="24"
              value={ttlHours}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTtlHours(e.target.value)}
            />
          </div>
          {create.isError && (
            <p className="text-xs text-[var(--color-danger)]">
              {create.error instanceof Error ? create.error.message : 'Mount creation failed'}
            </p>
          )}
          <div className="flex gap-2">
            <Button
              onClick={() => create.mutate()}
              disabled={!targetWorkspaceId || create.isPending}
              loading={create.isPending}
            >
              {!create.isPending && <Zap className="h-4 w-4 mr-1" />}
              {create.isPending ? 'Starting mount…' : 'Mount'}
            </Button>
            <Button variant="secondary" onClick={onClose} disabled={create.isPending}>Cancel</Button>
          </div>
        </div>
      )}
    </AppModal>
  );
};

interface InplaceMountPanelProps {
  entityId: string;
  runId: string;
}

export const InplaceMountPanel: React.FC<InplaceMountPanelProps> = ({ entityId, runId }) => {
  const queryClient = useQueryClient();
  // Spec 214 FR-028: Mount/Delete are Admin+Operator server-side — don't render them for
  // read roles (their clicks 403'd).
  const { isOperatorOrAbove } = useAuth();
  const canManage = isOperatorOrAbove();
  const [showCreate, setShowCreate] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);

  const { data: mounts, isLoading, error } = useQuery<InplaceMount[]>({
    queryKey: ['inplace-mounts', 'lakehouse', entityId],
    queryFn: () => mountApi.list('lakehouse', entityId),
    // Poll fast while a mount is still provisioning so it flips to Active promptly;
    // otherwise back off to a gentle 60s refresh.
    refetchInterval: (query) =>
      (query.state.data ?? []).some((m) => m.status === 'Provisioning') ? 8_000 : 60_000,
  });

  const deleteMutation = useMutation({
    mutationFn: (mountId: string) => mountApi.delete(mountId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['inplace-mounts'] });
      setDeleting(null);
    },
  });

  // Server returns non-Deleted mounts (Provisioning / Active / Failed).
  const visibleMounts = mounts ?? [];
  const liveCount = visibleMounts.filter((m) => m.status === 'Active' || m.status === 'Provisioning').length;

  return (
    <Card>
      <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--color-border)]">
        <div className="flex items-center gap-2">
          <Zap className="h-4 w-4 text-[var(--color-accent)]" />
          <span className="text-sm font-medium text-[var(--color-text-primary)]">Query in place</span>
          {liveCount > 0 && (
            <Badge variant="info">{liveCount} active</Badge>
          )}
        </div>
        {canManage && (
          <Button size="sm" onClick={() => setShowCreate(true)}>
            <Plus className="h-3.5 w-3.5 mr-1" />
            Mount
          </Button>
        )}
      </div>

      {isLoading && <LoadingState rows={2} />}
      {error && <ErrorState onRetry={() => undefined} />}
      {!isLoading && !error && visibleMounts.length === 0 && (
        <EmptyState
          title="No active mounts"
          message="Create a mount to query this recovery point's tables without a full restore."
        />
      )}
      {visibleMounts.length > 0 && (
        <DataTable<InplaceMount>
          columns={[
            {
              key: 'shortcutName',
              header: 'Lakehouse name',
              render: (row: InplaceMount) => (
                <span className="font-mono text-xs text-[var(--color-text-primary)]">{row.shortcutName}</span>
              ),
            },
            {
              key: 'expiresAt',
              header: 'Status / Expires',
              render: (row: InplaceMount) => {
                if (row.status === 'Provisioning') {
                  return <Badge variant="info">Provisioning…</Badge>;
                }
                if (row.status === 'Failed') {
                  return <Badge variant="danger">Failed</Badge>;
                }
                return <Badge variant={expiryVariant(row.expiresAt)}>{fmtExpiry(row.expiresAt)}</Badge>;
              },
            },
            {
              key: 'owner',
              header: 'Owner',
              render: (row: InplaceMount) => (
                <span className="text-xs text-[var(--color-text-secondary)]">{row.owner}</span>
              ),
            },
            {
              key: 'id',
              header: '' as React.ReactNode,
              width: '3rem',
              render: (row: InplaceMount) =>
                canManage ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      if (confirm('Remove this mount? The shortcut lakehouse will be deleted.')) {
                        setDeleting(row.id);
                        deleteMutation.mutate(row.id);
                      }
                    }}
                    disabled={deleting === row.id}
                    aria-label="Remove mount"
                  >
                    <Trash2 className="h-3.5 w-3.5 text-[var(--color-danger)]" />
                  </Button>
                ) : null,
            },
          ]}
          rows={visibleMounts}
          rowKey={(m: InplaceMount) => m.id}
        />
      )}

      {showCreate && (
        <CreateMountModal
          entityId={entityId}
          runId={runId}
          onClose={() => setShowCreate(false)}
        />
      )}
    </Card>
  );
};
